import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { safeReturnPath } from './returnPath';

/**
 * The way back from a deep-link, rendered wherever one can land.
 *
 * Browser-back stops working the moment you do anything at the destination —
 * switch a tab, open a drawer, follow a second link — which is exactly what you
 * came to do. So the exit has to be explicit, labelled with where it goes, and
 * survive moving around.
 *
 * Renders nothing when there is no trail, so it is safe to drop at the top of
 * any screen: most visits are not deep-links.
 */
export interface InnerLevel {
  /** Where closing the level lands, for the link's href. */
  path: string;
  label: string;
  /** Pops the level (see useDrillLevel) instead of pushing its parent. */
  onBack: () => void;
}

export default function ReturnTrail({
  className = '',
  fallback,
  level,
}: {
  className?: string;
  /**
   * A level open inside this screen. It outranks `?from=`: the way back from a
   * provider's editor is the providers list, and only from there is it wherever
   * you came from.
   */
  level?: InnerLevel | null;
  /**
   * A trail to render when there is no `?from=`. For screens that still receive
   * an older, bespoke return param — `/trending?from_brainstorm=` is the one —
   * so those links keep working without a second back button in a second style
   * sitting above this one.
   */
  fallback?: { path: string; label: string };
}) {
  const trail = useReturnTrail(fallback, level);
  if (!trail) return null;
  const { path, label } = trail;

  return (
    <div className={`mb-3 ${className}`}>
      <Link
        to={path}
        onClick={
          level
            ? (e) => {
                e.preventDefault();
                level.onBack();
              }
            : undefined
        }
        className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
      >
        <span aria-hidden="true">←</span>
        Back to {label}
      </Link>
    </div>
  );
}

/**
 * Where the way back goes, or null when there is none.
 *
 * SPLIT OUT OF THE COMPONENT so a host can lay out a row for the trail only when
 * one exists. `PageHeader` puts the breadcrumb and the back button on the same
 * line; rendering that row unconditionally would leave an empty 12px band on
 * every screen reached normally, which is most of them.
 */
export function useReturnTrail(
  fallback?: { path: string; label: string },
  level?: InnerLevel | null,
): { path: string; label: string } | null {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Same pathname as here by design, so it skips the self-link check below.
  if (level) return { path: level.path, label: level.label };

  const fromParam = safeReturnPath(searchParams.get('from'));
  const path = fromParam || (fallback ? safeReturnPath(fallback.path) : null);
  if (!path) return null;

  // A TRAIL BACK TO WHERE YOU ALREADY ARE IS A DEAD END THAT LOOKS LIVE, and it
  // is what a `fallback` produces on the screen it names: the product homes
  // rendered "← Back to home" while sitting on home, because the fallback fires
  // whenever there is no `?from=`.
  //
  // Fixed here rather than by asking every caller to check, because a caller
  // CANNOT check — a fallback is a static prop and the current route is not.
  // The same rule the app switcher applies to the shell you are already in.
  if (path.split('?')[0] === location.pathname) return null;

  const label = fromParam
    ? searchParams.get('from_label') || 'where you were'
    : fallback!.label;
  return { path, label };
}
